'use client'

import React from 'react'
import { cn } from '@/lib/utils'
import { useState } from 'react'
import { HugeiconsIcon } from '@hugeicons/react'
import { Csv01Icon, Image02Icon, Video01Icon, Pdf01Icon, File01Icon } from '@hugeicons-pro/core-stroke-rounded'
import { PencilEdit01Icon } from '@hugeicons-pro/core-solid-rounded'
import { parseContentSegments } from '@/lib/mentions/tokens'
import { MentionPopover } from '@/components/mentions/MentionPopover'

/**
 * Renders user message content with URLs auto-linked AND mention
 * tokens (`[[@kind:uuid|display]]`) rendered as MentionPopover badges.
 *
 * Mentions are parsed FIRST so token contents never get re-scanned for
 * URLs (a mention like `[[@brand:uuid|atrium.run]]` would otherwise
 * regex-match the display string as a URL).
 *
 * Stephen 2026-05-12: user bubbles were showing the raw token text
 * (`[[@campaign:...|name]]`) instead of just the badge. Same bug class
 * as the assistant-side ChatMarkdown, which already does this.
 */
const URL_REGEX = /https?:\/\/[^\s]+/g

function linkifyText(text: string, keyPrefix: string): React.ReactNode[] {
  const parts: React.ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null
  URL_REGEX.lastIndex = 0
  while ((match = URL_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }
    parts.push(
      <a key={`${keyPrefix}-url-${match.index}`} href={match[0]} target="_blank" rel="noopener noreferrer" className="underline hover:no-underline break-all">
        {match[0]}
      </a>
    )
    lastIndex = match.index + match[0].length
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }
  return parts
}

export function renderUserContent(content: string): React.ReactNode {
  const segments = parseContentSegments(content)
  const out: React.ReactNode[] = []
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i]
    if (seg.type === 'mention') {
      out.push(
        <MentionPopover
          key={`mention-${i}`}
          kind={seg.kind}
          id={seg.id}
          display={seg.display}
        />
      )
    } else {
      out.push(...linkifyText(seg.value, `text-${i}`))
    }
  }
  return <span className="whitespace-pre-wrap">{out}</span>
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function FileTypeIcon({ fileType }: { fileType: string | null | undefined }) {
  const cls = 'size-4 text-accent-600 shrink-0'
  // Stephen 2026-06-18: legacy chat_attachments rows may have file_type = null
  // (pre-defense before app/api/chat/upload/route.ts:155 fell back to
  // 'application/octet-stream'). Workspace-switch into CKE loads that
  // history; without this guard `.startsWith` crashes the lobby layout.
  const t = fileType ?? ''
  if (t === 'text/csv' || t === 'application/vnd.ms-excel' || t === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
    return <HugeiconsIcon icon={Csv01Icon} className={cls} />
  if (t.startsWith('image/'))
    return <HugeiconsIcon icon={Image02Icon} className={cls} />
  if (t.startsWith('video/'))
    return <HugeiconsIcon icon={Video01Icon} className={cls} />
  if (t === 'application/pdf')
    return <HugeiconsIcon icon={Pdf01Icon} className={cls} />
  return <HugeiconsIcon icon={File01Icon} className={cls} />
}

export interface ChatUserMessageProps {
  messageIndex?: number
  quotedTexts?: Array<{ text: string; messageIndex?: number }>
  content: string
  channel?: string
  attachments?: Array<{ id: string; file_name: string; file_type: string; file_size: number }>
  onResend?: (messageIndex: number, newContent: string) => void
  /**
   * When true, the inner content is capped to a centered narrow column while
   * the outer (hover/group) wrapper stays full width. Used when the chat
   * panel is in viewport-fullscreen mode so user bubbles don't get pushed to
   * the far edge on a wide monitor.
   */
  narrow?: boolean
}

export function ChatUserMessage({ messageIndex, quotedTexts, content, channel, attachments, onResend, narrow = false }: ChatUserMessageProps) {
  const [hovered, setHovered] = useState(false)
  const [editing, setEditing] = useState(false)
  const [editValue, setEditValue] = useState(content)

  const channelBadge =
    channel && channel !== 'web' ? (
      <span className="mt-1 text-[10px] text-[#939294]/80">
        via{' '}
        {channel === 'telegram'
          ? '✈️ Telegram'
          : channel === 'slack'
            ? '💬 Slack'
            : channel === 'imessage'
              ? '💬 iMessage'
              : channel}
      </span>
    ) : null

  const handleSubmitEdit = () => {
    if (!editValue.trim() || messageIndex === undefined || !onResend) return
    setEditing(false)
    onResend(messageIndex, editValue.trim())
  }

  // When `narrow` is true, an inner wrapper caps the content to a centered
  // max-w-3xl column. The outer hover container stays full width to match the
  // assistant rows, but the bubble (already right-aligned via items-end) lives
  // inside the narrow column instead of on the far right edge of the viewport.
  // `display: contents` keeps the non-narrow layout byte-identical to today.
  const narrowColCls = narrow ? 'mx-auto w-full max-w-3xl xl:max-w-4xl flex flex-col items-end' : 'contents'
  return (
    <div
      className="mt-4 mb-8 flex flex-col items-end"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className={narrowColCls}>
      {attachments && attachments.length > 0 && (
        <div className="max-w-[85%] mb-1 space-y-1">
          {attachments.map((att) => (
            <div
              key={att.id}
              className="flex items-center gap-2 bg-accent-600/10 border border-accent-600/20 rounded-lg px-3 py-1.5"
            >
              <FileTypeIcon fileType={att.file_type} />
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium text-atrium-black truncate">{att.file_name}</div>
                <div className="text-[10px] text-[#939294]">{formatFileSize(att.file_size)}</div>
              </div>
            </div>
          ))}
        </div>
      )}
      {quotedTexts && quotedTexts.length > 0 && (
        <div className="max-w-[85%] mb-1 space-y-1">
          {quotedTexts.map((q, qi) => (
            <div
              key={qi}
              onClick={() => {
                if (q.messageIndex !== undefined) {
                  const el = document.querySelector(`[data-msg-index="${q.messageIndex}"]`)
                  el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }
              }}
              className={cn(
                'bg-atrium-content border-l-2 border-accent-600 px-3 py-1.5 rounded-r-md text-xs text-atrium-black/70',
                q.messageIndex !== undefined && 'cursor-pointer hover:bg-atrium-content/80 transition-colors',
              )}
            >
              <span className="block text-[10px] text-[#939294] mb-0.5 font-medium">Quoted</span>
              <span className="block line-clamp-2">{q.text}</span>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-end gap-1.5 max-w-[85%]">
        {/* Edit button — appears on hover, left of bubble */}
        {onResend && messageIndex !== undefined && !editing && (
          <button
            onClick={() => { setEditValue(content); setEditing(true) }}
            className={cn(
              'mb-1 shrink-0 p-1 rounded-md text-atrium-black/30 hover:text-atrium-black/60 transition-opacity transition-colors',
              hovered ? 'opacity-100' : 'opacity-0 pointer-events-none'
            )}
            aria-label="Edit message"
          >
            <HugeiconsIcon icon={PencilEdit01Icon} className="size-3.5" />
          </button>
        )}
        {editing ? (
          <div className="flex-1 flex flex-col gap-1.5">
            <textarea
              autoFocus
              value={editValue}
              onChange={e => setEditValue(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmitEdit() }
                if (e.key === 'Escape') setEditing(false)
              }}
              className="w-full rounded-xl rounded-br-sm bg-accent-600/10 border border-accent-600/40 px-4 py-2.5 text-sm leading-relaxed text-atrium-black resize-none focus:outline-none focus:ring-1 focus:ring-accent-600/50"
              rows={Math.max(2, editValue.split('\n').length)}
            />
            <div className="flex gap-1.5 justify-end">
              <button
                onClick={() => setEditing(false)}
                className="px-2.5 py-1 text-xs text-atrium-black/60 hover:text-atrium-black transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitEdit}
                disabled={!editValue.trim()}
                className="px-3 py-1 text-xs font-medium bg-accent-600 text-white rounded-lg hover:bg-accent-600/90 transition-colors disabled:opacity-50"
              >
                Send
              </button>
            </div>
          </div>
        ) : (
          <div className="rounded-xl rounded-br-xs bg-black px-4 py-2.5 text-sm leading-relaxed text-white">
            {renderUserContent(content)}
          </div>
        )}
      </div>
      {channelBadge ? <div className="flex items-center gap-1.5 mt-0.5">{channelBadge}</div> : null}
      </div>
    </div>
  )
}
